import * as Sentry from '@sentry/remix';
import type {
  BasicNode,
  TiptapState,
  TPostContentElement,
  TPostSubscriberBreak,
  TRootElement,
} from '@shared/dream-components';
import type { WritableDraft } from 'immer';
import { produce } from 'immer';

import type { PostMergeTags } from '~/interfaces/post_merge_tags';

const SUBSCRIBER_ID_MERGE_TAG = '{{subscriber_id}}';
const EMAIL_MERGE_TAG = '{{email}}';

export type ProcessorFunctionOptions = {
  subscriberBreakElementData: TPostSubscriberBreak['attrs']['data'];
  hasSubscription: boolean;
  postMergeTags?: PostMergeTags;
  subscriberId?: string;
  subscriberEmail?: string;
  postTiptapState?: TRootElement | null;
};

export type ProcessorFunction = (
  postContentNode: WritableDraft<TPostContentElement>,
  options?: ProcessorFunctionOptions,
) => void;

export function process(
  postContentNode: TPostContentElement,
  processors: ProcessorFunction[],
  options?: ProcessorFunctionOptions,
): TPostContentElement {
  const processedNodeContent = produce(postContentNode, (draft) => {
    processors.filter(Boolean).forEach((processor) => {
      processor(draft, options);
    });
  });

  return processedNodeContent;
}

const processSubscriberGate = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  try {
    if (!Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
      return;
    }

    // find the subscriber break node
    const subscriberBreakIndex = draft.attrs.data.content.findIndex(
      (node: BasicNode) => node.type === 'subscriberBreak',
    );

    // there is no subscriber break
    if (subscriberBreakIndex === -1) return;

    // Inject data required to render Subscriber break element
    draft.attrs.data.content[subscriberBreakIndex] = {
      ...draft.attrs.data.content[subscriberBreakIndex],
      attrs: {
        data: {
          ...(options?.subscriberBreakElementData ? { ...options.subscriberBreakElementData } : {}),
        },
      },
    };

    // remove all contents after the subscriber break
    draft.attrs.data.content = draft.attrs.data.content.slice(0, subscriberBreakIndex + 1);
  } catch (error) {
    Sentry.captureException(error);
  }
};

// Example {{foo}} {{foo bar}} {{foo|bar}} {{ foo | bar }} {{ foo bar | baz buz }}
const MERGE_TAG_PATTERN = /\{\{\s*([^|}]+?)\s*(?:\|\s*([^|}]+?)\s*)?\}\}/g;

/**
 * Recursively walks an object/array tree and replaces merge tags in string values.
 * This approach is safer than JSON.stringify/parse because it:
 * - Avoids JSON parsing errors from user content
 * - Only replaces in actual string values, not in JSON structure
 * - Handles deeply nested objects correctly
 */
const replaceMergeTagsInValue = (value: unknown, mergeTags: PostMergeTags): unknown => {
  // Handle strings - this is where merge tag replacement happens
  if (typeof value === 'string') {
    let result = value;

    // Find all merge tags in the string
    const foundMergeTags = result.match(MERGE_TAG_PATTERN);
    if (!foundMergeTags) {
      return result;
    }

    foundMergeTags.forEach((tag) => {
      // Simple merge tag (example: {{subscriber_id}})
      // biome-ignore lint/suspicious/noPrototypeBuiltins: Need to check own properties safely
      if (Object.prototype.hasOwnProperty.call(mergeTags, tag)) {
        result = result.replaceAll(tag, mergeTags[tag]);
        return;
      }

      // Merge tags with default value (example: {{first_name|Guest}})
      const isMergeTagWithDefaultValue = tag.includes('|');
      if (isMergeTagWithDefaultValue) {
        try {
          const [tagName, tagDefaultValue] = tag.replace(/^\{\{|\}\}$/g, '').split('|');
          const tagNameWithoutSpaces = tagName.replaceAll(' ', '');
          const replacementValue = mergeTags[`{{${tagNameWithoutSpaces}}}`] || tagDefaultValue.trimStart().trimEnd();
          result = result.replaceAll(tag, replacementValue);
        } catch {
          // Fail gracefully for malformed default value syntax
        }
        return;
      }

      // Tag with spaces (example: {{ subscriber_id }})
      const extractedTagName = tag.replace(/^\{\{|\}\}$/g, '').trim();
      if (mergeTags[`{{${extractedTagName}}}`]) {
        result = result.replaceAll(tag, mergeTags[`{{${extractedTagName}}}`]);
      }
    });

    return result;
  }

  // Handle arrays - recurse into each element
  if (Array.isArray(value)) {
    return value.map((item) => replaceMergeTagsInValue(item, mergeTags));
  }

  // Handle objects - recurse into each property
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = replaceMergeTagsInValue(val, mergeTags);
    }
    return result;
  }

  // Primitives (numbers, booleans, null) - return as-is
  return value;
};

const processMergeTags = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  if (!Array.isArray(draft.attrs.data.content) || !draft.attrs.data.content.length) {
    return;
  }

  try {
    // Default merge tags, combined with any additional merge tags provided in options
    const mergeTags: PostMergeTags = {
      [SUBSCRIBER_ID_MERGE_TAG]: options?.subscriberId || SUBSCRIBER_ID_MERGE_TAG,
      [EMAIL_MERGE_TAG]: options?.subscriberEmail || EMAIL_MERGE_TAG,
      ...(options?.postMergeTags || {}),
    };

    // Use object-walking approach instead of JSON.stringify/parse [WEB-5442]
    // This is safer because it replaces values directly in the object tree,
    // avoiding JSON parsing errors from user-provided content
    draft.attrs.data.content = produce(draft.attrs.data.content, (contentDraft: TiptapState) => {
      return replaceMergeTagsInValue(contentDraft, mergeTags) as TiptapState;
    });
  } catch (error) {
    // Capture detailed context for debugging if the new approach still fails [WEB-5442]
    Sentry.withScope((scope) => {
      scope.setContext('merge_tags_error', {
        error_message: error instanceof Error ? error.message : String(error),
        content_structure: JSON.stringify(draft.attrs.data.content, null, 2).substring(0, 1000),
        merge_tags: JSON.stringify(options?.postMergeTags || {}, null, 2).substring(0, 500),
        subscriber_id: options?.subscriberId,
        subscriber_email: options?.subscriberEmail,
      });
      Sentry.captureException(error);
    });

    // Re-throw to maintain existing error behavior
    throw error;
  }
};

const LINK_TARGET_KEYS = new Set(['href', 'url', 'captionUrl']);
const UNRESOLVED_MERGE_TAG_PATTERN = /\{\{[^{}]*\}\}/g;

const stripMergeTagsFromLinkTargets = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    let changed = false;
    const items = value.map((item) => {
      const next = stripMergeTagsFromLinkTargets(item);
      changed ||= next !== item;
      return next;
    });

    return changed ? items : value;
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  let changed = false;
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    if (LINK_TARGET_KEYS.has(key) && typeof val === 'string' && val.includes('{{')) {
      result[key] = val.replace(UNRESOLVED_MERGE_TAG_PATTERN, '');
      changed ||= result[key] !== val;
      continue;
    }

    const next = stripMergeTagsFromLinkTargets(val);
    changed ||= next !== val;
    result[key] = next;
  }

  return changed ? result : value;
};

const processUnresolvedMergeTagLinks = (draft: WritableDraft<TPostContentElement>) => {
  const content = draft.attrs.data.content;
  if (!Array.isArray(content) || !content.length) {
    return;
  }

  const stripped = stripMergeTagsFromLinkTargets(content);
  if (stripped === content) {
    return;
  }

  draft.attrs.data.content = stripped as TiptapState;
};

// Selects the content source for rendering: the freshly-fetched, swapped
// `post_tiptap_state` (from the meta_information XHR) when available, otherwise the
// content already present on the node from the initial server render. Both sources are
// produced by the backend `render_json` swapper, which now also sets `renderAsHtml` on
// HTML-expanding merge-tag text leaves.
const processData = (draft: WritableDraft<TPostContentElement>, options?: ProcessorFunctionOptions) => {
  if (Array.isArray(options?.postTiptapState?.content) && options?.postTiptapState?.content.length > 0) {
    draft.attrs.data.content = options.postTiptapState.content;
  }
};

export const processors = {
  processSubscriberGate,
  processMergeTags,
  processUnresolvedMergeTagLinks,
  processData,
};
